// Van Life — static game data and balance constants. See RULES.md.

import { Suit, ZoneCard, ChallengeCard, ZoneCardId, ChallengeCardId } from './types';

// ── Game balance constants ────────────────────────────────────────────────────
export const TOTAL_WILDS     = 9;  // wild suit: ranks 1-9, unique (no ties, physically producible)
export const INITIAL_WILDS   = 4;  // wilds in pool at game start (match 1 deck = 27 + 4 = 31)
export const WILDS_PER_MATCH = 1;  // wilds moved from wild deck to pool after each match
export const DEAL_SIZE       = 10; // cards dealt to each player before discard
export const HAND_SIZE       = 8;  // cards per player after discard (= tricks per match)

export const SUITS: Suit[] = ['Green', 'Blue', 'Tan'];
export const PLAYER_COLORS = ['#E74C3C', '#3498DB'];

export const ZONE_CARDS: ZoneCard[] = [
  // Hard
  { id: 'summit-exact',  name: 'Summit Exact',  description: 'Tracker ends at exactly position 4' },
  { id: 'momentum',      name: 'Momentum',      description: 'Tracker ends at 2 or lower, or 6 or higher' },
  // Medium (3 positions)
  { id: 'tundra-winds',  name: 'Tundra Winds',  description: 'Tracker ends in positions 2–4 (P1 wins more tricks)' },
  { id: 'coastal-drift', name: 'Coastal Drift', description: 'Tracker ends in positions 4–6 (P2 wins more tricks)' },
  { id: 'ridge-line',    name: 'Ridge Line',    description: 'Tracker ends in positions 3–5 (balanced)' },
  // Medium (4 positions)
  { id: 'river-delta',   name: 'River Delta',   description: 'Tracker ends in positions 2–5' },
  { id: 'safe-passage',  name: 'Safe Passage',  description: 'Tracker never reaches 1 or 7 during the match' },
  { id: 'divergence',    name: 'Divergence',    description: 'Tracker ends in 1–3 or 5–7 (one player dominates)' },
  // Easy (5 positions)
  { id: 'wilderness',    name: 'Wilderness',    description: 'Tracker ends in positions 2–6 (wide window)' },
  { id: 'prairie-wind',  name: 'Prairie Wind',  description: 'Tracker ends in positions 1–5 (P1-leaning)' },
  { id: 'sea-breeze',    name: 'Sea Breeze',    description: 'Tracker ends in positions 3–7 (P2-leaning)' },
  // Very easy (6 positions)
  { id: 'open-country',  name: 'Open Country',  description: 'Tracker ends in positions 2–7 (very wide window)' },
];

export const CHALLENGE_CARDS: ChallengeCard[] = [
  // Hard
  { id: 'bookends',       name: 'Bookends',       description: 'Win both the 1st and last tricks' },
  { id: 'alternating',    name: 'Alternating',    description: 'Win at least 1 trick; never win consecutive tricks' },
  { id: 'odd-tricks',     name: 'Odd Tricks',     description: 'Win at least 1 trick; all wins on odd-numbered tricks (1,3,5,7)' },
  { id: 'suit-sampler',   name: 'Suit Sampler',   description: 'Win at least one trick with a Green, one Blue, one Tan card' },
  { id: 'streak',         name: 'Streak',         description: 'Win 3 or more consecutive tricks' },
  { id: 'clean-sweep',    name: 'Clean Sweep',    description: 'Win a trick where your card is the only one of its suit played' },
  // Medium
  { id: 'low-road',       name: 'Low Road',       description: 'Win at least one trick with a rank 1 or 2 card' },
  { id: 'iron-patience',  name: 'Iron Patience',  description: 'Win none of the first 3 tricks' },
  { id: 'strong-finish',  name: 'Strong Finish',  description: 'Win more tricks in the 2nd half than the 1st half' },
  { id: 'deck-beater',    name: 'Deck Beater',    description: 'Win more tricks than the Park Deck wins' },
  { id: 'double-run',     name: 'Double Run',     description: 'Win 2 or more consecutive tricks' },
  { id: 'pack-light',     name: 'Pack Light',     description: 'Win between 2 and 5 tricks total' },
  // Easy
  { id: 'first-blood',    name: 'First Blood',    description: 'Win the 1st trick' },
  { id: 'last-stand',     name: 'Last Stand',     description: 'Win the last trick' },
  { id: 'first-or-last',  name: 'First or Last',  description: 'Win trick 1 or the last trick (or both)' },
  { id: 'low-gear',       name: 'Low Gear',       description: 'Win at least one trick with a rank 1–4 card' },
  // Very easy
  { id: 'early-bird',     name: 'Early Bird',     description: 'Win at least 1 trick in the first half (tricks 1–4)' },
  { id: 'night-owl',      name: 'Night Owl',      description: 'Win at least 1 trick in the second half (tricks 5–8)' },
  { id: 'pioneer-spirit', name: 'Pioneer Spirit', description: 'Win at least 1 trick' },
];

export const PARK_NAMES: Record<string, string> = {
  'kobuk':      'Kobuk Valley',
  'gates':      'Gates of the Arctic',
  'denali':     'Denali',
  'lake-clark': 'Lake Clark',
  'katmai':     'Katmai',
  'kenai':      'Kenai Fjords',
  'wrangell':   'Wrangell-St. Elias',
  'glacier':    'Glacier Bay',
};

export const PARK_IDS = Object.keys(PARK_NAMES);

// Higher = easier to achieve; used by challenge player's movement bot
export const CHALLENGE_DIFFICULTY: Record<ChallengeCardId, number> = {
  'pioneer-spirit': 10, 'early-bird': 9, 'night-owl': 9, 'first-or-last': 8, 'low-gear': 8,
  'first-blood': 7, 'last-stand': 7, 'pack-light': 7, 'deck-beater': 6, 'double-run': 6,
  'strong-finish': 5, 'iron-patience': 5, 'low-road': 4,
  'suit-sampler': 3, 'alternating': 3, 'odd-tricks': 3,
  'bookends': 2, 'streak': 2, 'clean-sweep': 1,
};

export const PARK_ADJACENCY: Record<string, string[]> = {
  'kobuk':      ['gates', 'denali', 'lake-clark'],
  'gates':      ['kobuk', 'denali'],
  'denali':     ['gates', 'kobuk', 'lake-clark', 'kenai', 'wrangell'],
  'lake-clark': ['denali', 'katmai', 'kenai', 'kobuk'],
  'katmai':     ['lake-clark', 'kenai', 'glacier'],
  'kenai':      ['denali', 'lake-clark', 'katmai', 'wrangell', 'glacier'],
  'wrangell':   ['denali', 'kenai', 'glacier'],
  'glacier':    ['wrangell', 'katmai', 'kenai'],
};

// Valid final tracker positions per zone card; also drives the track player's bot.
export function zoneValidFinals(id: ZoneCardId): number[] {
  switch (id) {
    case 'summit-exact':  return [4];
    case 'momentum':      return [1, 2, 6, 7];
    case 'tundra-winds':  return [2, 3, 4];
    case 'coastal-drift': return [4, 5, 6];
    case 'ridge-line':    return [3, 4, 5];
    case 'river-delta':   return [2, 3, 4, 5];
    case 'safe-passage':  return [2, 3, 4, 5, 6];
    case 'divergence':    return [1, 2, 3, 5, 6, 7];
    case 'wilderness':    return [2, 3, 4, 5, 6];
    case 'prairie-wind':  return [1, 2, 3, 4, 5];
    case 'sea-breeze':    return [3, 4, 5, 6, 7];
    case 'open-country':  return [2, 3, 4, 5, 6, 7];
  }
}
